import React, { memo, useCallback, useMemo } from 'react';

import { Button } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import ButtonGroupWrapper from '@sharedCR/coreDesignSystem/Buttons/ButtonGroupWrapper';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import PropTypes from 'prop-types';

import { ConstructionTestIds } from '../../Construction.testIds';

import CurrentDrawReviewPane from './components/CurrentDrawReviewPane';
import { getDrawListConfig } from './DrawListSection.config';
import { getDrawListData } from './DrawListSection.utils';

const DrawListSection = ({
  currentDrawRequest,
  drawRequests,
  hasBudget,
  isAddDrawRequestDisabled,
  isEditAllowed,
  isLoading,
  onAddDrawRequest,
  onEditBudget,
  onReviewDraw,
}) => {
  const config = useMemo(() => getDrawListConfig({ onReview: onReviewDraw }), [onReviewDraw]);
  const data = useMemo(() => getDrawListData(drawRequests), [drawRequests]);

  const onRowClick = useCallback(row => onReviewDraw(row.id), [onReviewDraw]);

  const actionContent = (
    <ButtonGroupWrapper>
      <Button
        data-testid={ConstructionTestIds.DRAW_LIST_EDIT_BUDGET_BUTTON}
        disabled={!isEditAllowed}
        variant="outlined"
        onClick={onEditBudget}
      >
        {hasBudget ? 'Edit Budget' : 'Create Budget'}
      </Button>
      <Button
        data-testid={ConstructionTestIds.DRAW_LIST_ADD_BUTTON}
        disabled={isAddDrawRequestDisabled}
        variant="contained"
        onClick={onAddDrawRequest}
      >
        Add Draw Request
      </Button>
    </ButtonGroupWrapper>
  );

  return (
    <PageBlock
      withAction
      actionContent={actionContent}
      data-testid={ConstructionTestIds.DRAW_LIST_SECTION}
      title="Draw Requests"
    >
      {currentDrawRequest && (
        <CurrentDrawReviewPane drawRequest={currentDrawRequest} onOpenReview={onReviewDraw} />
      )}
      <TableCDS
        config={config}
        data={data}
        isLoading={isLoading}
        title=""
        onRowClick={onRowClick}
      />
    </PageBlock>
  );
};

DrawListSection.propTypes = {
  onAddDrawRequest: PropTypes.func.isRequired,
  onEditBudget: PropTypes.func.isRequired,
  onReviewDraw: PropTypes.func.isRequired,
  hasBudget: PropTypes.bool,
  isAddDrawRequestDisabled: PropTypes.bool,
  isEditAllowed: PropTypes.bool,
  isLoading: PropTypes.bool,
  currentDrawRequest: PropTypes.object,
  drawRequests: PropTypes.array,
};

export default memo(DrawListSection);
